import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { useAuthUser, useAuthUserAccounts } from '@nbx/frontend-helpers/subscribe';
import { Context } from 'state/store';
import { setMenuRef, setMenuToggleRef, setNavToggleRef, setOverlay } from 'state/creators';
import OVERLAYS from 'constants/overlays';

import styles from './styles.module.scss';
import MobileNavigation from '../MobileNavigation/MobileNavigation';
import MobileAccounts from '../MobileAccounts/MobileAccounts';

const microapps = window.MICROFRONTENDS_CONFIG;

function HeaderMenuMobile(props) {
  const [state, dispatch] = React.useContext(Context);
  const user = useAuthUser();
  const userAccounts = useAuthUserAccounts();
  const menuRef = React.useRef(null);
  const navToggleRef = React.useRef(null);
  const menuToggleRef = React.useRef(null);

  // set Refs
  React.useEffect(() => {
    dispatch(setMenuRef(menuRef.current));
    dispatch(setNavToggleRef(menuRef.current));
    dispatch(setMenuToggleRef(menuRef.current));
    return () => {
      dispatch(setMenuRef(null));
      dispatch(setNavToggleRef(null));
      dispatch(setMenuToggleRef(null));
    };
  }, [dispatch]);

  const menuOrNavVisible = state.overlay === OVERLAYS.menu || state.overlay === OVERLAYS.nav;

  React.useEffect(() => {
    const microfrontend = document.getElementById('microfrontend_hook');
    const modal = document.getElementById('modal_hook');
    const footer = document.getElementById('footer_hook');
    if (menuOrNavVisible) {
      footer.classList.add('hidden');
      microfrontend.classList.add('hidden');
      modal.classList.add('hidden');
    } else {
      const modalIsOpen = modal.hasChildNodes();
      if (!modalIsOpen) footer.classList.remove('hidden');
      if (!modalIsOpen) microfrontend.classList.remove('hidden');
      modal.classList.remove('hidden');
    }
  }, [menuOrNavVisible]);

  React.useEffect(() => {
    // Runs when this component unmounts e.g. switching from mobile to desktop breakpoint
    return () => {
      const microfrontend = document.getElementById('microfrontend_hook');
      const modal = document.getElementById('modal_hook');
      const footer = document.getElementById('footer_hook');
      footer.classList.remove('hidden');
      microfrontend.classList.remove('hidden');
      modal.classList.remove('hidden');
    };
  }, []);

  const getAccountName = userAccount => {
    if (!userAccount || !user) return null;
    let accountName = '';
    if (typeof userAccount.name === 'string') accountName = userAccount.name;
    else if (typeof user.firstName === 'string') accountName = user.firstName;
    else accountName = user.email;
    return accountName;
  };

  const toggleNavCloseMenu = () => {
    if (state.overlay === OVERLAYS.menu) props.toggleMenu();
    else if (state.overlay === OVERLAYS.nav) dispatch(setOverlay(null));
    else dispatch(setOverlay(OVERLAYS.nav));
  };

  const openOTCModal = () => dispatch(setOverlay(OVERLAYS.otc));
  const openQuickBuy = () => dispatch(setOverlay(OVERLAYS.quickbuy));
  const openAddFunds = () => dispatch(setOverlay(OVERLAYS.addfunds));

  const currentAccount = userAccounts?.find(userAccount => state.activeAccount === userAccount.id);
  const currentAccountName = getAccountName(currentAccount);

  return (
    <>
      {user !== null ? (
        <Link className={styles.logo} to={microapps?.routes?.dashboards?.path}>
          <nbx-icon icon="nbx_logo_mobile" />
        </Link>
      ) : (
        <Link className={styles.logo} to="/">
          <nbx-icon icon="nbx_logo_mobile" />
        </Link>
      )}
      <div className={classnames([styles.menu, { [styles.menuVisible]: menuOrNavVisible }])} ref={menuRef}>
        <div className={styles.menuHeader}>
          {menuOrNavVisible ? (
            <button
              type="button"
              className={styles.menuToggle}
              onClick={toggleNavCloseMenu}
              ref={navToggleRef}
              id="menuToggle">
              {<nbx-icon icon="close_v2" />}
            </button>
          ) : (
            <nbx-avatar
              size="small"
              name={currentAccountName}
              onClick={props.toggleMenu}
              ref={menuToggleRef}
            />
          )}
          {!menuOrNavVisible && (
            <button
              type="button"
              className={styles.menuToggle}
              onClick={toggleNavCloseMenu}
              ref={navToggleRef}
              id="menuToggle">
              {<nbx-icon icon="menu_v2" />}
            </button>
          )}
        </div>

        <div className={classnames([styles.menuItems, { [styles.visible]: menuOrNavVisible }])}>
          {state.overlay === OVERLAYS.nav && (
            <MobileNavigation
              user={user}
              toggleNav={toggleNavCloseMenu}
              openOTCModal={openOTCModal}
              openQuickBuy={openQuickBuy}
              openAddFunds={openAddFunds}
            />
          )}
          {state.overlay === OVERLAYS.menu && (
            <MobileAccounts
              user={user}
              userAccounts={userAccounts}
              activeAccount={state.activeAccount}
              switchAccount={props.switchAccount}
              switchingAccount={state.switchingAccount}
              toggleMenu={props.toggleMenu}
              toggleTheme={props.toggleTheme}
              signOut={props.signOut}
            />
          )}
        </div>
      </div>
    </>
  );
}

HeaderMenuMobile.propTypes = {
  switchAccount: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  toggleTheme: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired
};

export default HeaderMenuMobile;
